import { createRouter, createWebHistory } from 'vue-router';
import { loadLayoutMiddleware } from '@/router/middleware/loadLayout.middleware';
import { AppLayoutsEnum } from '@/layouts/layouts.types';
import { getResearch } from '@/services/ApiRequests';
import { useResearch } from '@/stores/Research';
import { Research } from '@/domain/Research';
import { useCompareFirmStore } from '@/stores/CompareFirm';
import capitalizeFirstLetter from '@/utils/capitalize-first-letter';
import { isBlackDomain } from '@/utils/is-black-domain';

async function checkResearchFailed(to: any) {
  const researchId = to.params.id_research as string;

  try {
    const response = await getResearch(researchId, { silent: true });
    if (response) {
      if (response.data.status === 'failed') {
        useResearch().$patch({
          research: new Research(response.data),
          status: 'failed',
          mockComplited: true,
          existingResearch: true,
          errorMessage: response.data.error_message || null,
        });
        useCompareFirmStore().$patch({ step: 2 });
        return { name: 'home' };
      }

      if (response.data.status !== 'complete') {
        useResearch().$patch({
          research: new Research(response.data),
          existingResearch: true,
          isShowAnimation: true,
        });
        useCompareFirmStore().$patch({ step: 2 });
        return { name: 'home' };
      }

      if (!useResearch().research) {
        useResearch().$patch({
          research: new Research(response.data),
        });
      }
    }
  } catch {
    return { name: 'not-found' };
  }
}

// @ts-ignore
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/DomainHomeView.vue'),
    },
    {
      path: '/lp',
      name: 'lp',
      component: () => import('@/views/LandingPage.vue'),
    },
    {
      path: '/:id_research/pdf',
      name: 'pdf',
      component: () => import('@/views/PDFViews.vue'),
      meta: {
        layout: AppLayoutsEnum.empty,
      },
    },
    {
      path: '/:id_research',
      name: 'research',
      component: () => '',
      beforeEnter: async function (to) {
        const researchId = to.params.id_research as string;

        try {
          const response = await getResearch(researchId, { silent: true });
          if (response) {
            useResearch().$patch({
              research: new Research(response.data),
            });

            if (response.data.status === 'failed') {
              useCompareFirmStore().$patch({ step: 2 });
              useResearch().$patch({
                status: 'failed',
                mockComplited: true,
                existingResearch: true,
                errorMessage: response.data.error_message || null,
              });
              await router.push({ name: 'home' });
            } else if (
              response.data.contact_information &&
              response.data.status === 'complete'
            ) {
              await router.push(`/results/${researchId}/overall-score`);
            } else if (
              response.data.contact_information &&
              response.data.status !== 'complete'
            ) {
              useCompareFirmStore().$patch({
                step: 2,
              });
              useResearch().$patch({
                existingResearch: true,
              });

              await router.push({ name: 'home' });
            } else {
              useCompareFirmStore().$patch({
                step: 2,
              });

              const research = response.data;
              const businessName =
                research?.name.split(`(${research.place_id})`)[0].trim() || '';
              const compareFirmObject = {
                addressInformation: { name: businessName },
                selectedPracticeArea: {
                  label: capitalizeFirstLetter(research?.practice_area),
                },
                manuallyWebsiteUrl: research?.domain || '',
              };
              useCompareFirmStore().$patch(compareFirmObject);

              await router.push({ name: 'home' });
            }
          }
        } catch (err) {
          await router.push({ name: 'not-found' });
        }
      },
    },
    {
      path: '/results/:id_research/overall-score',
      name: 'overall-score',
      component: () => import('@/views/OverallScore.vue'),
      meta: {
        layout: AppLayoutsEnum.dashboard,
      },
      beforeEnter: checkResearchFailed,
    },
    {
      path: '/results/:id_research/web-presence',
      name: 'web-presence',
      component: () => import('@/views/WebPresence.vue'),
      meta: {
        layout: AppLayoutsEnum.dashboard,
      },
      beforeEnter: checkResearchFailed,
    },
    {
      path: '/results/:id_research/firm-reputation',
      name: 'firm-reputation',
      component: () => import('@/views/FirmReputation.vue'),
      meta: {
        layout: AppLayoutsEnum.dashboard,
      },
      beforeEnter: checkResearchFailed,
    },
    {
      path: '/results/:id_research/website-performance',
      name: 'website-performance',
      component: () => import('@/views/WebsitePerformance.vue'),
      meta: {
        layout: AppLayoutsEnum.dashboard,
      },
      beforeEnter: checkResearchFailed,
    },
    {
      path: '/results/:id_research/website-authority',
      name: 'website-authority',
      component: () => import('@/views/WebsiteAuthority.vue'),
      meta: {
        layout: AppLayoutsEnum.dashboard,
      },
      beforeEnter: async function (to) {
        const failedRedirect = await checkResearchFailed(to);
        if (failedRedirect) return failedRedirect;

        const researchId = to.params.id_research as string;

        if (!useResearch().research) {
          try {
            const response = await getResearch(researchId, { silent: true });

            if (response) {
              useResearch().$patch({
                research: new Research(response.data),
              });

              if (!useResearch().research?.isAuthority()) {
                return { name: 'not-found' };
              }
            }
          } catch (err) {
            return { name: 'not-found' };
          }
        }
      },
    },
    {
      path: '/request-consultation',
      name: 'request-consultation',
      component: () => import('@/views/RequestConsultation.vue'),
      meta: {
        layout: AppLayoutsEnum.consultation,
      },
    },
    {
      path: '/:id_research/:subpath(.*)',
      redirect: (to) => {
        return `/results/${to.params.id_research}/${to.params.subpath}`;
      },
    },
    {
      path: '/results/:id_research',
      redirect: (to) => {
        return `/results/${to.params.id_research}/overall-score`;
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
    },
  ],
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach((to) => {
  // Домашняя страница переключается по домену:
  // - compare2.* -> black-лендинг + empty layout
  // - compare.* (и остальные) -> дефолтная домашка + default layout
  if (to.name === 'home') {
    const isStep2 = to.query.step === '2';
    to.meta.layout =
      isBlackDomain() && !isStep2
        ? AppLayoutsEnum.empty
        : AppLayoutsEnum.default;
  }
});

router.beforeEach(loadLayoutMiddleware);

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      // @ts-ignore
      window.location = to.fullPath;
    }
  }
});

export default router;
